import { useInView } from '../../../hooks/useInView';

const features = [
  {
    number: '01',
    icon: 'ri-focus-3-line',
    title: 'ターゲット設計から\n伴走します',
    desc: '「どんな学生と出会いたいか」を一緒に言語化。媒体の検索条件・フィルター設定を最適化し、貴社の採用ターゲットに本当に合う学生に届くスカウトを設計します。',
    tags: ['ターゲット定義', '媒体最適化', '条件設計'],
    bg: 'bg-[#1a2e4a]',
    textColor: 'text-white',
    descColor: 'text-white/70',
    tagBg: 'bg-white/10',
    tagText: 'text-white/80',
  },
  {
    number: '02',
    icon: 'ri-edit-line',
    title: '個別最適化された\n文面で送信',
    desc: '学生一人ひとりの経験・志向性に合わせ、スカウト文面をカスタマイズ。画一的な一斉送信ではなく、「自分に向けたメッセージ」と感じてもらえる文面設計で返信率を高めます。',
    tags: ['文面カスタマイズ', '個別最適化', '返信率向上'],
    bg: 'bg-white',
    textColor: 'text-[#1a2e4a]',
    descColor: 'text-[#666]',
    tagBg: 'bg-[#f0f4f9]',
    tagText: 'text-[#1a2e4a]',
  },
  {
    number: '03',
    icon: 'ri-line-chart-line',
    title: 'データで継続的に\n改善します',
    desc: '送信数・開封率・返信率・承認率を毎週レポートとして共有。数値の変化を見ながら文面・ターゲット・送信タイミングを継続的に改善し、母集団形成を安定させます。',
    tags: ['週次レポート', 'PDCA', '改善提案'],
    bg: 'bg-[#f7f5f2]',
    textColor: 'text-[#1a2e4a]',
    descColor: 'text-[#666]',
    tagBg: 'bg-white',
    tagText: 'text-[#1a2e4a]',
  },
];

export default function FeaturesSection() {
  const { ref, inView } = useInView();

  return (
    <section id="features" className="py-10 md:py-20 bg-[#f0ece6]">
      <div ref={ref} className="max-w-6xl mx-auto px-4 sm:px-6">
        {/* Header */}
        <div className={`text-center mb-8 md:mb-14 transition-all duration-700 ease-out ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <span className="text-xs tracking-widest text-[#E8601C] font-medium uppercase mb-3 block">
            FEATURES
          </span>
          <h2
            className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#1a2e4a] leading-tight"
            style={{ fontFamily: "'Noto Serif JP', serif" }}
          >
            選ばれる3つの理由
          </h2>
          <p className="text-[#666] text-sm mt-4 leading-relaxed">
            スカウト代行とは「送る作業」ではなく、<br className="hidden md:block" />
            採用成果につながる仕組みを構築することです。
          </p>
        </div>

        {/* Feature Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6">
          {features.map((f, i) => (
            <div
              key={i}
              className={`${f.bg} rounded-2xl p-6 sm:p-8 flex flex-col gap-5 border border-transparent transition-all duration-700 ease-out ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
              style={{ transitionDelay: inView ? `${i * 150 + 250}ms` : '0ms' }}
            >
              {/* Number + Icon */}
              <div className="flex items-center justify-between">
                <span
                  className={`text-4xl font-bold ${f.textColor} opacity-20`}
                  style={{ fontFamily: "'Noto Serif JP', serif" }}
                >
                  {f.number}
                </span>
                <div className={`w-11 h-11 flex items-center justify-center rounded-xl ${i === 0 ? 'bg-white/10' : 'bg-[#1a2e4a]/8'}`}>
                  <i className={`${f.icon} text-xl ${i === 0 ? 'text-[#E8601C]' : 'text-[#1a2e4a]'}`} />
                </div>
              </div>

              {/* Title */}
              <h3
                className={`text-lg font-bold ${f.textColor} leading-snug whitespace-pre-line`}
                style={{ fontFamily: "'Noto Serif JP', serif" }}
              >
                {f.title}
              </h3>

              {/* Description */}
              <p className={`text-sm ${f.descColor} leading-relaxed flex-1`}>{f.desc}</p>

              {/* Tags */}
              <div className="flex flex-wrap gap-2">
                {f.tags.map((tag) => (
                  <span
                    key={tag}
                    className={`text-xs px-3 py-1 rounded-full ${f.tagBg} ${f.tagText} font-medium`}
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Bottom Image */}
        <div className={`mt-6 sm:mt-10 rounded-2xl overflow-hidden relative transition-all duration-700 ease-out ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} style={{ transitionDelay: inView ? '700ms' : '0ms' }}>
          <img
            src="https://public.readdy.ai/ai/img_res/a137c9e275ede57798c0b79dfa489855.jpg"
            alt="データ分析によるスカウト改善のイメージ"
            className="w-full h-36 sm:h-48 object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#1a2e4a]/80 to-[#1a2e4a]/30 flex items-center px-6 sm:px-10">
            <p
              className="text-white text-lg sm:text-xl md:text-2xl font-bold leading-snug"
              style={{ fontFamily: "'Noto Serif JP', serif" }}
            >
              「送って終わり」ではなく、<br />
              <span className="text-[#E8601C]">成果が出るまで</span>伴走します。
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
